import {FormControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {ActivatedRoute} from '@angular/router';

import {APPMAX, IntegrationsService} from '../../../shared/services/integrations.service';
import {PlatformsService} from '../../../shared/services/platforms.service';
import {ToastService} from '../../../shared/components/toast/toast.service';
import {CopyService} from '../../../shared/services/copy.service';
import {Integration} from '../../../models/Integration';
import {Platform} from 'src/app/models/Platform';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {Observable, Subject, of} from 'rxjs';
import {catchError, takeUntil} from 'rxjs/operators';
import {environment} from '../../../../environments/environment';

@Component({
  selector: 'app-integration',
  templateUrl: './integration.component.html',
  styleUrls: ['./integration.component.scss']
})
export class IntegrationComponent implements OnInit, OnChanges, OnDestroy {

  title = 'Nova integração';

  @Input() hidden = true;
  @Input() integration: Integration;
  @Output() saved = new EventEmitter<boolean>();
  @Output() toggleHidden = new EventEmitter<boolean>();

  @ViewChild('modalHelpMP') modalHelpMP: NgbModalRef;
  @ViewChild('integration') inputName: ElementRef;

  form: UntypedFormGroup;

  /** Gravação em curso — trava e gira o botão Salvar. */
  isSaving = false;
  integrationId: string;

  multiPlatform: boolean;
  integrations: Integration[] = [];
  filteredIntegrations: Integration[] = [];
  enableMP: boolean;

  // SMS-003: dados do webhook canônico (preenchidos ao editar uma integração existente)
  loadedIntegration: Integration | null = null;
  webhookUrl: string | null = null;
  curlSnippet = '';

  platforms: Platform[];

  timer: any = null;
  @ViewChild('platformInput') platformInput: ElementRef<HTMLInputElement>;
  platformControl = new FormControl<Platform | null>(null);
  filteredPlatform$!: Observable<Platform[]>;

  private destroy$ = new Subject<void>();
  /** A plataforma selecionada exige token (campo "Token" visível). */
  showToken = false;
  /** Há token gravado para ESTA plataforma: o campo vira opcional e vazio mantém o atual. */
  hasStoredToken = false;

  constructor(
    private fb: UntypedFormBuilder,
    private toastService: ToastService,
    private activatedRoute: ActivatedRoute,
    private platformService: PlatformsService,
    private modalService: NgbModal,
    private integrationsService: IntegrationsService,
    private copyService: CopyService
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      id: [''],
      name: ['', [Validators.required, Validators.minLength(5)]],
      platform_id: ['', Validators.required],
      alternative_integration_id: [''],
      url: [''],
      acURL: ['', Validators.pattern('^$|^https:\\/\\/(?:[a-zA-Z0-9-]+\\.activehosted\\.com|[a-zA-Z0-9-]+\\.api-us\\d\\.com)$')],
      acKey: [''],
      // Nunca pré-preenchido: a API não devolve o token, só `has_token`.
      token: ['']
    });

    // Assinatura única (a do ngOnChanges é recriada a cada mudança de input).
    // Trocar de plataforma descarta o token digitado: ele pertence à conta da
    // plataforma anterior.
    this.platformControl.valueChanges.subscribe(() => {
      this.form.patchValue({ token: '' }, { emitEvent: false });
      this.updateTokenValidators();
    });

    this.activatedRoute
      .params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.integrationId = null;
        this.loadedIntegration = null;
        this.webhookUrl = null;
        this.curlSnippet = '';
        if (params.id) {
          this.integrationId = params.id;
          this.title = 'Alterar integração';

          this.integrationsService.findById(params.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe(integration => {
              this.form.patchValue(integration);
              if (integration && integration.platform) {
                this.platformControl.setValue(integration.platform);
              }
              this.applyWebhookInfo(integration);
            });
        }
      });

    // SMS-003: pré-seleção de template via query param (smart suggestion - SMS-006)
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params.template_id) {
          this.preselectPlatform(params.template_id);
        }
      });

    this.integrationsService
      .listAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe(integrations => this.integrations = integrations);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * SMS-003: dado um id de plataforma/template (vindo de ?template_id=X),
   * busca o objeto Platform completo e o seta no autocomplete — o save() lê
   * de platformControl.value, não do form control 'platform_id'.
   */
  private preselectPlatform(platformId: string): void {
    this.platformService.listAll()
      .pipe(
        catchError(() => of([] as Platform[])),
        takeUntil(this.destroy$)
      )
      .subscribe(platforms => {
        const match = (platforms || []).find(p => p.id === platformId);
        if (match) {
          this.platformControl.setValue(match);
        }
      });
  }

  /**
   * SMS-003: monta a URL canônica do webhook e o snippet curl a partir do slug.
   */
  private applyWebhookInfo(integration: Integration): void {
    this.loadedIntegration = integration;

    if (integration && integration.slug) {
      this.webhookUrl = `${environment.postbackUrl}/${integration.slug}/webhook`;
      this.curlSnippet = `curl -X POST ${this.webhookUrl} \\\n  -H "Content-Type: application/json" \\\n  -d '{"lead_id":"123456"}'`;
    } else {
      this.webhookUrl = null;
      this.curlSnippet = '';
    }
  }

  /**
   * SMS-003: copia a URL do webhook.
   */
  copyWebhookUrl(): void {
    this.copyService.copy('webhook-url', 'URL do webhook copiada!');
  }

  /**
   * SMS-003: copia o snippet curl.
   */
  copyCurl(): void {
    this.copyService.copy('webhook-curl', 'Snippet curl copiado!');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.integration) {

      this.integrationId = this.integration.id;

      if (this.integration.acURL) {
        this.integration.acURL = this.integration.acURL.replace('/api/3', '');
      }

      this.platformControl.patchValue(this.integration.platform);

      this.form.patchValue(this.integration);
      this.enableMP = this.integration.platform_id === APPMAX;
      this.multiPlatform = !!this.integration.alternative_integration_id;
      this.filteredIntegrations = this.integrations.filter(integration => integration.id !== this.integration.id);
      this.applyWebhookInfo(this.integration);
    } else {
      this.integrationId = null;
      this.platformControl.patchValue(null);
      this.form?.reset();
      this.loadedIntegration = null;
      this.webhookUrl = null;
      this.curlSnippet = '';
    }

    this.platformControl.valueChanges.subscribe(platform => {
      let platformId = '';
      if (platform?.id) {
        platformId = platform.id;
      }

      if (!platformId) {
        this.platformInput.nativeElement.value = '';
        this.filteredPlatform$ = null;
      }

      this.form.patchValue({ platform_id: platformId });
    });

    this.updateTokenValidators();

    setTimeout(() => this.inputName?.nativeElement.focus(), 200);
  }

  /**
   * Campo "Token": aparece quando a plataforma exige; é obrigatório na criação e
   * opcional na edição de integração que já tem token gravado para a MESMA
   * plataforma (o back mantém o gravado quando o campo vai ausente).
   */
  updateTokenValidators(): void {
    const tokenControl = this.form?.get('token');
    if (!tokenControl) {
      return;
    }

    const platform = this.platformControl.value;
    this.showToken = !!platform?.requires_token;
    this.hasStoredToken = this.showToken
      && !!this.integration?.has_token
      && this.integration?.platform_id === platform?.id;

    tokenControl.setValidators(this.showToken && !this.hasStoredToken ? [Validators.required] : []);
    tokenControl.updateValueAndValidity();
  }

  copy(): void {
    this.copyService.copy('url', 'URL de integração copiada!');
  }

  save(): void {
    const selectedPlatform = this.platformControl.value;
    if (!selectedPlatform || !selectedPlatform.id) {
      this.toastService.error('Selecione uma plataforma.');
      return;
    }

    const integration = this.form.getRawValue() as Integration;

    integration.platform_id = selectedPlatform.id;

    const token = (integration.token || '').trim();
    if (this.showToken && token) {
      integration.token = token;
    } else {
      delete integration.token;
    }

    const successMessage = integration.id
    ? 'Integração alterado com sucesso!'
    : 'Integração cadastrado com sucesso!';

    const errorMessage = integration.id
      ? 'Erro ao alterar integração!'
      : 'Erro ao cadastrar integração!';

    this.isSaving = true;
    this.integrationsService
      .save(integration)
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.toastService.success(successMessage);
          this.saved.emit(true);
          this.form.reset();
          // A lista local alimenta o select de integração alternativa: não guardar o token.
          const stored = { ...integration };
          delete stored.token;
          this.integrations.push(stored);
        },
        error: err => {
          this.isSaving = false;
          // SEC-02 (`{ error: { code, message } }`) ou o formato legado (`{ message }`).
          this.toastService.error(err.error?.error?.message || err.error?.message || errorMessage);
        }
      });
  }

  openHelpMP(): void {
    this.modalService.open(this.modalHelpMP);
  }

  disableOrEnableMP(): void {
    if (!this.multiPlatform) {
      this.form.patchValue({ alternative_integration_id: '' });
    }
  }

  filterPlatform(): void {
    if (this.timer) {
      clearTimeout(this.timer);
    }

    const filterValue = this.platformInput.nativeElement.value?.trim().toLowerCase();

    const platform = this.platformControl.value;
    if (platform && filterValue.length < this.displayPlatform(platform).length) {
      this.platformControl.patchValue(null);
      this.filteredPlatform$ = null;
      return;
    }

    if (filterValue.length >= 3) {
      this.timer = setTimeout(() => {
        this.filteredPlatform$ = this.platformService.filter(filterValue);
      }, 200);
    }
  }

  displayPlatform(platform?: Platform): string {
    if (platform) {
      return platform.name;
    }

    return '';
  }

}
